'use client';

import { useState, useEffect } from 'react';
import type { AuctionDetailsResponse } from '../../services/generated/Api';
import { AUCTION_UPDATE_INTERVAL } from './constants';

interface UseAuctionTimerOptions {
  auction: AuctionDetailsResponse | null;
  onExpired?: () => void;
}

interface UseAuctionTimerResult {
  localTimeRemaining: number;
}

export const useAuctionTimer = ({
  auction,
  onExpired,
}: UseAuctionTimerOptions): UseAuctionTimerResult => {
  const [localTimeRemaining, setLocalTimeRemaining] = useState<number>(0);

  // Use stable primitive for dependency
  const auctionEndTime = auction?.endTime;

  useEffect(() => {
    if (!auctionEndTime) return;

    const endTime = new Date(auctionEndTime).getTime();
    const now = Date.now();
    const remaining = Math.max(0, endTime - now);
    setLocalTimeRemaining(remaining);

    if (remaining <= 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const newRemaining = Math.max(0, endTime - now);
      setLocalTimeRemaining(newRemaining);

      if (newRemaining <= 0) {
        if (onExpired) {
          onExpired();
        }
        clearInterval(interval);
      }
    }, AUCTION_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [auctionEndTime, onExpired]);

  return {
    localTimeRemaining,
  };
};
